/** Capability codes aligned with public.business_type_capabilities.capability_code */
export type BusinessCapability =
  | 'pos'
  | 'cash_sessions'
  | 'catalog'
  | 'inventory'
  | 'sales_history'
  | 'reports'
  | 'expenses'
  | 'employees'
  | 'customer_credit_fiado'
  | 'barcode_scan'
  | 'misc_quick_sale'
  | 'promotions'
  | 'price_lists'
  | 'customer_loyalty'
  | 'external_services'
  | 'product_variants'
  | 'menu_modifiers'
  | 'product_combos'
  | 'product_pharmacy_fields'
  | 'weight_uom'
  | 'catalog_virtual'
  | 'shelf_labels'
  | 'service_catalog'
  | 'service_quotes'
  | 'service_packages'
  | 'appointments'
  | 'delivery_fulfillment'
  | 'tables'
  | 'kitchen_tickets'
  | 'pickup_display'
  | 'split_bill'
  | 'pos_tips'
  | 'fiscal_electronic'
  | 'mp_qr'
  | 'mp_oauth'
  | 'purchases'
  | 'recipes'
  | 'work_orders'
  | 'commissions'
  | 'on_duty'
  | 'checks';

export type { UnitOfMeasure } from '@shared/quantity';

/** Rubro ERP code from DB (`business_types.code`). New industries do not require a TS union update. */
export type BusinessTypeCode = string;

export type CountryProfile = {
  default_currency: string;
  default_locale: string;
  tax_system_code: string;
};

export type OrganizationBusinessProfile = {
  country_code: string;
  business_type_code: BusinessTypeCode;
  business_type_name: string;
  business_config: Record<string, unknown>;
  /** Optional identity under quick_service_food (pizzeria|burger|parrilla|cafe). */
  vertical_flavor?: string | null;
  /** Free-text rubro when the merchant chose Otros in onboarding / change rubro. */
  requested_vertical_label?: string | null;
  capabilities: BusinessCapability[];
  country: CountryProfile;
  /** Canonical org logo (hub + inherited storefront profiles). */
  organization_logo_url?: string | null;
  organization_logo_focal_x?: number;
  organization_logo_focal_y?: number;
  organization_logo_zoom?: number;
};

export type BusinessTypeListItem = {
  code: BusinessTypeCode;
  display_name: string;
  description: string | null;
  sort_order: number;
  onboarding_pack_code: string | null;
  onboarding_picker_group: string | null;
  /** Emoji del tile en el picker ("¿Qué vendés?"). Viene de la BD; null = sin chip. */
  picker_icon?: string | null;
  /** Sinónimos que matchean el search del picker (además de nombre/desc/grupo). */
  picker_keywords?: string[] | null;
};

/** Offline / legacy display fallbacks for known codes. Prefer RPC display_name. */
export const BUSINESS_TYPE_DISPLAY_NAMES: Record<string, string> = {
  retail_counter: 'Kiosco',
  neighborhood_store: 'Mercado',
  services_beauty: 'Peluquería y barbería',
  services_estetica: 'Estética',
  services_workshop: 'Taller',
  food_retail: 'Para llevar',
  clothing_retail: 'Indumentaria',
  beauty_retail: 'Cosmética',
  stationery_retail: 'Librería',
  weight_retail: 'Verdulería',
  hardware_retail: 'Ferretería',
  pharmacy_basic: 'Farmacia',
  quick_service_food: 'Gastronomía',
  restaurant_full: 'Restaurante',
  general_retail: 'Otros',
  bakery_retail: 'Panadería',
  diet_retail: 'Dietética',
  services_veterinary: 'Veterinaria',
  optica_retail: 'Óptica',
  laundry_service: 'Lavandería',
  auto_parts_retail: 'Autopartes',
  tech_retail: 'Celulares',
  toy_retail: 'Juguetería',
  sports_retail: 'Deportes',
  home_retail: 'Hogar',
  drinks_retail: 'Tragos y bebidas',
  tienda_retail: 'Tienda',
  jewelry_retail: 'Joyería y accesorios',
  court_rental: 'Alquiler de canchas',
};

function isBusinessTypeCodeKey(value: string): boolean {
  return /^[a-z][a-z0-9_]*$/.test(value) && value.includes('_');
}

/** Resuelve el nombre legible del rubro cuando el payload trae la clave cruda. */
export function resolveBusinessTypeDisplayLabel(
  code: BusinessTypeCode | string | null | undefined,
  name?: string | null,
  catalog?: Pick<BusinessTypeListItem, 'code' | 'display_name'>[] | null,
): string {
  const codeKey = typeof code === 'string' ? code.trim() : '';
  const trimmedName = typeof name === 'string' ? name.trim() : '';

  if (trimmedName && trimmedName !== codeKey && !isBusinessTypeCodeKey(trimmedName)) {
    return trimmedName;
  }

  const fromCatalog = catalog?.find((item) => item.code === codeKey)?.display_name?.trim();
  if (fromCatalog && fromCatalog !== codeKey) return fromCatalog;

  if (codeKey && codeKey in BUSINESS_TYPE_DISPLAY_NAMES) {
    return BUSINESS_TYPE_DISPLAY_NAMES[codeKey];
  }

  return trimmedName || codeKey || 'Rubro';
}

/** Chrome de org: label pedido (Otros) o nombre canónico del rubro ERP. */
export function resolveOrganizationRubroDisplayLabel(
  profile: Pick<
    OrganizationBusinessProfile,
    'requested_vertical_label' | 'business_type_name' | 'business_type_code'
  > | null | undefined,
  catalog?: Pick<BusinessTypeListItem, 'code' | 'display_name'>[] | null,
): string {
  if (!profile) return 'Rubro';
  const requested = profile.requested_vertical_label?.trim();
  if (requested) return requested;
  return resolveBusinessTypeDisplayLabel(
    profile.business_type_code,
    profile.business_type_name,
    catalog,
  );
}

export function parseBusinessProfilePayload(raw: unknown): OrganizationBusinessProfile | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const capabilities = Array.isArray(o.capabilities)
    ? (o.capabilities.filter((c) => typeof c === 'string') as BusinessCapability[])
    : [];
  const countryRaw = o.country;
  const country =
    countryRaw && typeof countryRaw === 'object'
      ? {
          default_currency: String((countryRaw as Record<string, unknown>).default_currency ?? 'ARS'),
          default_locale: String((countryRaw as Record<string, unknown>).default_locale ?? 'es-AR'),
          tax_system_code: String((countryRaw as Record<string, unknown>).tax_system_code ?? 'ar_afip'),
        }
      : { default_currency: 'ARS', default_locale: 'es-AR', tax_system_code: 'ar_afip' };

  if (typeof o.business_type_code !== 'string' || typeof o.country_code !== 'string') {
    return null;
  }

  const businessTypeCode = o.business_type_code;

  const verticalFlavor =
    typeof o.vertical_flavor === 'string'
      ? o.vertical_flavor
      : o.business_config &&
          typeof o.business_config === 'object' &&
          typeof (o.business_config as Record<string, unknown>).vertical_flavor === 'string'
        ? String((o.business_config as Record<string, unknown>).vertical_flavor)
        : null;

  const requestedVerticalLabel =
    typeof o.requested_vertical_label === 'string'
      ? o.requested_vertical_label
      : null;

  const orgLogoRaw = o.organization_logo_url;
  const organization_logo_url =
    typeof orgLogoRaw === 'string' && orgLogoRaw.trim() ? orgLogoRaw.trim() : null;

  return {
    country_code: o.country_code,
    business_type_code: businessTypeCode,
    business_type_name: resolveBusinessTypeDisplayLabel(
      businessTypeCode,
      String(o.business_type_name ?? o.business_type_code),
    ),
    business_config:
      o.business_config && typeof o.business_config === 'object'
        ? (o.business_config as Record<string, unknown>)
        : {},
    vertical_flavor: verticalFlavor,
    requested_vertical_label: requestedVerticalLabel,
    organization_logo_url,
    organization_logo_focal_x:
      o.organization_logo_focal_x != null ? Number(o.organization_logo_focal_x) : 50,
    organization_logo_focal_y:
      o.organization_logo_focal_y != null ? Number(o.organization_logo_focal_y) : 50,
    organization_logo_zoom:
      o.organization_logo_zoom != null ? Number(o.organization_logo_zoom) : 1,
    capabilities,
    country,
  };
}

/** Normaliza respuestas RPC jsonb (array o string JSON) antes de parsear. */
export function normalizeBusinessTypeListRaw(raw: unknown): unknown {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return raw;
    try {
      return JSON.parse(trimmed) as unknown;
    } catch {
      return raw;
    }
  }
  return raw;
}

export function parseBusinessTypeList(raw: unknown): BusinessTypeListItem[] {
  const normalized = normalizeBusinessTypeListRaw(raw);
  if (!Array.isArray(normalized)) return [];
  return normalized
    .map((item): BusinessTypeListItem | null => {
      if (!item || typeof item !== 'object') return null;
      const o = item as Record<string, unknown>;
      if (typeof o.code !== 'string') return null;
      return {
        code: o.code,
        display_name: String(o.display_name ?? o.code),
        description: typeof o.description === 'string' ? o.description : null,
        sort_order: typeof o.sort_order === 'number' ? o.sort_order : 0,
        onboarding_pack_code:
          typeof o.onboarding_pack_code === 'string' ? o.onboarding_pack_code : null,
        onboarding_picker_group:
          typeof o.onboarding_picker_group === 'string' ? o.onboarding_picker_group : null,
        picker_icon: typeof o.picker_icon === 'string' ? o.picker_icon : null,
        picker_keywords: Array.isArray(o.picker_keywords)
          ? o.picker_keywords.filter((k): k is string => typeof k === 'string')
          : null,
      };
    })
    .filter((x): x is BusinessTypeListItem => x !== null)
    .sort((a, b) => a.sort_order - b.sort_order || a.display_name.localeCompare(b.display_name));
}
